import { Layout, LayoutItem } from "react-grid-layout";

import {
  GRID_COLUMNS,
  MAX_BLOCK_HEIGHT,
} from "src/cases/templates/caseBlocks/gridConstants";
import { CaseBlockConfigFormValue } from "src/cases/templates/formUtils/types";
import { CaseBlockPositioning } from "src/clients/case-management";

/**
 * Convert 1-indexed CaseBlockPositioning to 0-indexed RGL LayoutItem
 */
export const positionToLayoutItem = (
  block: CaseBlockConfigFormValue,
): LayoutItem | null => {
  const pos = block.case_block_positioning;

  return {
    i: block._internalId,
    x: pos.column - 1,
    y: pos.row - 1,
    w: pos.width,
    h: pos.height,
    minW: 2,
    maxW: GRID_COLUMNS,
    minH: 1,
    maxH: MAX_BLOCK_HEIGHT,
  };
};

/**
 * Convert 0-indexed RGL LayoutItem to 1-indexed CaseBlockPositioning
 */
export const layoutItemToPosition = (
  item: LayoutItem,
): CaseBlockPositioning => ({
  column: item.x + 1,
  row: item.y + 1,
  width: item.w,
  height: item.h,
});

/**
 * Convert array of blocks to RGL Layout
 */
export const blocksToLayout = (blocks: CaseBlockConfigFormValue[]): Layout =>
  blocks.map(positionToLayoutItem).filter((item) => item !== null);

/**
 * Apply RGL layout changes to blocks array
 * Returns null if no changes detected
 */
export const applyLayoutToBlocks = (
  blocks: CaseBlockConfigFormValue[],
  layout: Layout,
): CaseBlockConfigFormValue[] | null => {
  const layoutMap = new Map(layout.map((item) => [item.i, item]));
  let hasChanges = false;

  const updatedBlocks = blocks.map((block) => {
    const layoutItem = layoutMap.get(block._internalId);
    if (!layoutItem) return block;

    const newPos = layoutItemToPosition(layoutItem);
    const currPos = block.case_block_positioning;

    if (
      currPos.column === newPos.column &&
      currPos.row === newPos.row &&
      currPos.width === newPos.width &&
      currPos.height === newPos.height
    ) {
      return block;
    }

    hasChanges = true;
    return { ...block, case_block_positioning: newPos };
  });

  return hasChanges ? updatedBlocks : null;
};
